import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4plugins_overlapBuster from '@amcharts/amcharts4/plugins/overlapBuster';
import _ from 'lodash';
import PropTypes from 'prop-types';
import chartFilterSlice from '../../../../redux/slices/chartFilterSlice';
import { autoEscapeCharactersForCharts } from '../Chart.js';
import { COLORS, FONT_FAMILIES, FONT_SIZES } from '../../../../styles/constants';
import diamondShape from '../../../../images/charts/diamondShape.svg';
import circleShapePrimaryColor from '../../../../images/charts/circleShapePrimaryColor.svg';
import circleShapeSecondary2Color from '../../../../images/charts/circleShapeSecondary2Color.svg';
import { CHARTS_TRANSLATION_STRINGS } from '../chartConstant.js';

class BubbleChart extends Component {
	componentDidMount() {
		this.initChart();
	}

	componentDidUpdate(prevProps) {
		// Handle refreshing the chart when the dataset changes
		if (
			!_.isEqual(prevProps.productTableData, this.props.productTableData) ||
			!_.isEqual(prevProps.chartDefinition, this.props.chartDefinition)
		) {
			if (this.chart) {
				this.chart.dispose();
			}

			this.initChart();
		}
	}

	componentWillUnmount() {
		if (this.chart) {
			this.chart.dispose();
		}
	}

	initChart() {
		const { productTableData, chartDefinition, changeFilterValue } = this.props;
		const initialChartData = chartDefinition.dataList;
		let filteredChartData = initialChartData.filter(ichd =>
			productTableData.some(ptd => ptd[chartDefinition.opnCid] === ichd.opn)
		);
		am4core.options.commercialLicense = true;
		am4core.addLicense('CH2281-6107-0011-9579');

		var chart = am4core.create('chartdiv', am4charts.XYChart);
		chart.stroke = am4core.color('black');
		chart.strokeWidth = 0.5;
		chart.hiddenState.properties.opacity = 0; // this creates initial fade-in
		chart.paddingTop = 10;
		chart.paddingBottom = -10;
		chart.marginBottom = 0;
		chart.paddingRight = 20;
		chart.fontSize = FONT_SIZES.BODY2;
		chart.zoomOutButton.background.fill = am4core.color('gray');
		chart.zoomOutButton.background.states.getKey('hover').properties.fill = am4core.color(COLORS.BACKGROUND_GREY_DARK);
		chart.zoomOutButton.align = 'right';
		chart.zoomOutButton.valign = 'bottom';
		chart.zoomOutButton.marginBottom = 5;

		var title = chart.titles.create();
		//title.text = autoEscapeCharactersForCharts(chartDefinition.gfHeading);
		title.fontSize = 20;
		title.align = 'center';

		var valueAxisX = chart.xAxes.push(new am4charts.ValueAxis());
		valueAxisX.title.text = autoEscapeCharactersForCharts(chartDefinition.xaxeLabel);
		valueAxisX.title.fontWeight = 50;
		valueAxisX.title.fill = COLORS.TEXT_SECONDARY;
		valueAxisX.title.dy = -10;
		valueAxisX.renderer.minGridDistance = 40;
		valueAxisX.renderer.grid.template.disabled = false;
		valueAxisX.strokeWidth = 0.2;

		var valueAxisY = chart.yAxes.push(new am4charts.ValueAxis());
		valueAxisY.title.text = autoEscapeCharactersForCharts(chartDefinition.y1AxeLabel);
		valueAxisY.title.fontWeight = 50;
		valueAxisY.title.fill = COLORS.TEXT_SECONDARY;
		valueAxisY.renderer.grid.template.disabled = false;
		valueAxisY.strokeWidth = 0.2;

		chart.events.on('ready', function (ev) {
			valueAxisX.min = valueAxisX.minZoomed;
			valueAxisX.max = valueAxisX.maxZoomed;
			valueAxisX.strictMinMax = true;
		});

		var series = chart.series.push(new am4charts.LineSeries());
		series.dataFields.valueX = 'xwithFom';
		series.dataFields.valueY = 'ywithFom';
		series.dataFields.value = 'fomDiameter';
		series.strokeOpacity = 0;
		series.sequencedInterpolation = true;
		series.name = 'Series 1';

		var bullet = series.bullets.push(new am4core.Circle());
		bullet.adapter.add('fill', (value, target, key) => {
			return getColorForPart(target);
		});
		bullet.strokeOpacity = 0;
		bullet.strokeWidth = 2;
		bullet.fillOpacity = 0.7;
		bullet.stroke = am4core.color(' #eea638');
		series.heatRules.push({ target: bullet, min: 3, max: 18, property: 'radius' });

		let overlap = chart.plugins.push(new am4plugins_overlapBuster.OverlapBuster());
		overlap.targets.push(bullet);
		overlap.revealRatio = 0.3;

		bullet.tooltipText =
			getTooltipText() +
			'\n' +
			autoEscapeCharactersForCharts(chartDefinition.fomLabel) +
			' = {fom} ' +
			autoEscapeCharactersForCharts(chartDefinition.fomUnit);

		function getTooltipText() {
			return (
				'[bold]{opn} [/]\n ' +
				autoEscapeCharactersForCharts(chartDefinition.xlabel) +
				' = {valueX.value} ' +
				autoEscapeCharactersForCharts(chartDefinition.xunit) +
				'\n ' +
				autoEscapeCharactersForCharts(chartDefinition.ylabel) +
				' = {valueY.value} ' +
				autoEscapeCharactersForCharts(chartDefinition.yunit)
			);
		}

		function getColorForPart(target) {
			if (target._dataItem._dataContext.color === 'COLOR1') {
				return COLORS.PRIMARY;
			} else if (target._dataItem._dataContext.color === 'COLOR2') {
				return COLORS.SECONDARY2;
			} else {
				return COLORS.SECONDARY2;
			}
		}

		function gainLegendColorCsv(colorFromBE) {
			if (colorFromBE === 'COLOR1') {
				return circleShapePrimaryColor;
			} else if (colorFromBE === 'COLOR2') {
				return circleShapeSecondary2Color;
			} else {
				return circleShapeSecondary2Color;
			}
		}

		var hoverState = bullet.states.create('hover');
		hoverState.properties.fillOpacity = 1;
		hoverState.properties.strokeOpacity = 1;
		bullet.cursorOverStyle = am4core.MouseCursorStyle.pointer;

		bullet.adapter.add('tooltipY', function (tooltipY, target) {
			return -target.radius;
		});

		bullet.events.on('hit', event => {
			changeFilterValue({
				cid: chartDefinition.filterByCid,
				value: event.target._dataItem._dataContext.opn
			});
		});

		// Create series #2
		var series2 = chart.series.push(new am4charts.LineSeries());
		series2.dataFields.valueX = 'x';
		series2.dataFields.valueY = 'y';
		series2.dataFields.value = 1;
		series2.strokeOpacity = 0;
		series2.name = 'Series 2';

		var bullet2 = series2.bullets.push(new am4charts.Bullet());
		bullet2.adapter.add('fill', (value, target, key) => {
			return getColorForPart(target);
		});
		bullet2.tooltipText = 'x:{valueX} y:{valueY}';
		bullet2.layout = 'absolute';
		bullet2.rotation = 45;
		bullet2.tooltipText = getTooltipText() + '\n\n' + CHARTS_TRANSLATION_STRINGS.bubbleTooltip;

		bullet2.events.on('hit', event => {
			changeFilterValue({
				cid: chartDefinition.filterByCid,
				value: event.target._dataItem._dataContext.opn
			});
		});

		let overlap2 = chart.plugins.push(new am4plugins_overlapBuster.OverlapBuster());
		overlap2.targets.push(bullet2);
		overlap2.revealRatio = 0.3;

		var rectangle = bullet2.createChild(am4core.Rectangle);
		rectangle.verticalCenter = 'middle';
		rectangle.horizontalCenter = 'middle';
		rectangle.width = 6;
		rectangle.height = 6;
		rectangle.strokeOpacity = 0.7;
		rectangle.stroke = am4core.color('#ffffff');
		rectangle.nonScalingStroke = true;

		rectangle.verticalCenter = 'middle';
		rectangle.horizontalCenter = 'middle';

		chart.cursor = new am4charts.XYCursor();
		chart.cursor.behavior = 'zoomXY';

		let legend = new am4charts.Legend();
		let marker = legend.markers.template;
		marker.disposeChildren();
		legend.fontSize = 12;
		legend.parent = chart.chartContainer;
		legend.position = 'center';
		legend.paddingLeft = 80;
		legend.strokeWidth = 0;
		legend.itemContainers.template.clickable = false;
		legend.itemContainers.template.focusable = false;
		legend.data = gainLegendData();

		function gainLegendData() {
			const colorLegend = chartDefinition.colourLegend;
			let dataList = [];
			for (const key in colorLegend) {
				dataList.push({
					name: key,
					flag: gainLegendColorCsv(colorLegend[key])
				});
			}
			dataList.sort(function (a, b) {
				var nameA = a.name.toUpperCase();
				var nameB = b.name.toUpperCase();
				return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
			});
			dataList.push({
				name: 'FOM not available',
				flag: diamondShape
			});
			return dataList;
		}

		let legendIcon = marker.createChild(am4core.Image);
		legendIcon.width = 20;
		legendIcon.height = 20;
		legendIcon.verticalCenter = 'center';
		legendIcon.horizontalCenter = 'center';

		legendIcon.adapter.add('href', function (href, target) {
			return target.dataItem.dataContext.flag;
		});

		let labelMessage = chart.createChild(am4core.Label);
		labelMessage.text = '';
		labelMessage.fontSize = 14;
		labelMessage.fill = COLORS.TEXT_PRIMARY;
		labelMessage.strokeWidth = 0;
		labelMessage.isMeasured = false;
		labelMessage.x = am4core.percent(55);
		labelMessage.y = am4core.percent(25);
		labelMessage.horizontalCenter = 'middle';
		labelMessage.wrap = true;

		if (filteredChartData.length === 0 && productTableData.length === 0) {
			labelMessage.text = CHARTS_TRANSLATION_STRINGS.noProducts;
		} else if (filteredChartData.length === 0 && productTableData.length !== 0) {
			labelMessage.text = CHARTS_TRANSLATION_STRINGS.notAllData;
		}
		valueAxisX.events.on('sizechanged', function (ev) {
			var cell = ev.target;
			labelMessage.maxWidth = cell._prevLength - 100;
		});
		chart.data = filteredChartData;
	}

	render() {
		return (
			<div
				id="chartdiv"
				style={{
					width: '100%',
					height: '300px',
					fontFamily: FONT_FAMILIES
				}}
			/>
		);
	}
}

BubbleChart.propTypes = {
	productTableData: PropTypes.arrayOf(PropTypes.object).isRequired,
	chartDefinition: PropTypes.object.isRequired,
	changeFilterValue: PropTypes.func
};

const mapDispatchToProps = dispatch => ({
	changeFilterValue: filterValues => dispatch(chartFilterSlice.actions.updateFiltrationParameters(filterValues))
});

export default connect(null, mapDispatchToProps)(BubbleChart);
